import { useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import type { FAST } from '../../../types/prf';

interface FASTSectionProps {
  data: FAST;
  onSave: (data: FAST) => void;
  onBack: () => void;
}

export default function FASTSection({
  data,
  onSave,
  onBack,
}: FASTSectionProps) {
  const [formData, setFormData] = useState<FAST>(data);

  const handleDropdownChange = (field: keyof FAST, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  const assessmentOptions = [
    'No',
    'Yes',
    'Unable to assess',
  ];

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-foreground">F.A.S.T</h2>
              <p className="text-xs text-muted-foreground">Stroke assessment (Face, Arms, Speech, Time)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Face Drooping */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Face Drooping
            </label>
            <select
              value={formData.faceDropping ? 'Yes' : formData.faceDropping === false ? 'No' : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'Yes') {
                  setFormData(prev => ({ ...prev, faceDropping: true }));
                } else if (value === 'No') {
                  setFormData(prev => ({ ...prev, faceDropping: false }));
                } else {
                  setFormData(prev => ({ ...prev, faceDropping: undefined }));
                }
              }}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors appearance-none cursor-pointer"
            >
              <option value="">Select assessment</option>
              {assessmentOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Arm Weakness */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Arm Weakness
            </label>
            <select
              value={formData.armWeakness ? 'Yes' : formData.armWeakness === false ? 'No' : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'Yes') {
                  setFormData(prev => ({ ...prev, armWeakness: true }));
                } else if (value === 'No') {
                  setFormData(prev => ({ ...prev, armWeakness: false }));
                } else {
                  setFormData(prev => ({ ...prev, armWeakness: undefined }));
                }
              }}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors appearance-none cursor-pointer"
            >
              <option value="">Select assessment</option>
              {assessmentOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Speech Difficulty */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Speech Difficulty
            </label>
            <select
              value={formData.speechDifficulty ? 'Yes' : formData.speechDifficulty === false ? 'No' : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'Yes') {
                  setFormData(prev => ({ ...prev, speechDifficulty: true }));
                } else if (value === 'No') {
                  setFormData(prev => ({ ...prev, speechDifficulty: false }));
                } else {
                  setFormData(prev => ({ ...prev, speechDifficulty: undefined }));
                }
              }}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors appearance-none cursor-pointer"
            >
              <option value="">Select assessment</option>
              {assessmentOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Time of Onset */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Time of Onset
            </label>
            <input
              type="text"
              value={formData.timeOfOnset || ''}
              onChange={(e) => handleDropdownChange('timeOfOnset', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              placeholder="Eg: 14:30"
            />
          </div>

          {/* FAST Positive */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              FAST Positive
            </label>
            <select
              value={formData.fastPositive ? 'Yes' : formData.fastPositive === false ? 'No' : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'Yes') {
                  setFormData(prev => ({ ...prev, fastPositive: true }));
                } else if (value === 'No') {
                  setFormData(prev => ({ ...prev, fastPositive: false }));
                } else {
                  setFormData(prev => ({ ...prev, fastPositive: undefined }));
                }
              }}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors appearance-none cursor-pointer"
            >
              <option value="">Select assessment</option>
              {assessmentOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Additional Notes
            </label>
            <textarea
              value={formData.additionalNotes || ''}
              onChange={(e) => handleDropdownChange('additionalNotes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              placeholder="Any additional FAST assessment notes..."
            />
          </div>
        </div>
      </div>

      {/* Footer with Save Button */}
      <div className="flex-shrink-0 border-t border-border bg-card p-4">
        <button
          onClick={handleSave}
          className="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
      </div>
    </div>
  );
}
